import { useState, useEffect, useCallback } from "react";
import axios from "../customAxios.js";
import { dateToDatabase } from "../utils/functions";
import { useAuth } from "./useAuth.jsx";

// Auxiliary function to initialize filters from localStorage, outside the hook.
const getInitialState = (daysOffset, isAdmin, adminId) => {
  const savedStateJSON = localStorage.getItem("deliveriesFilter");
  const defaults = {
    filters: {
      startDate: new Date(
        new Date().setDate(new Date().getDate() - daysOffset),
      ),
      endDate: new Date(),
      statusFilter: "all",
      search: "",
      companyFilter: isAdmin ? "all" : null,
      adminFilter: isAdmin ? adminId : null,
      assignedDriverFilter: "all",
    },
    pagination: {
      page: 1,
      rowsPerPage: 20,
    },
  };

  if (savedStateJSON) {
    const savedState = JSON.parse(savedStateJSON);
    return {
      filters: {
        startDate: new Date(savedState.startDate),
        endDate: new Date(savedState.endDate),
        statusFilter: savedState.statusFilter || defaults.filters.statusFilter,
        assignedDriverFilter:
          savedState.assignedDriverFilter ||
          defaults.filters.assignedDriverFilter,
        search: savedState.search || defaults.filters.search,
        companyFilter:
          savedState.companyFilter || defaults.filters.companyFilter,
        adminFilter: savedState.adminFilter || defaults.filters.adminFilter,
      },
      pagination: {
        page: savedState.page || defaults.pagination.page,
        rowsPerPage: savedState.rowsPerPage || defaults.pagination.rowsPerPage,
      },
    };
  }
  return defaults;
};

const chunkArray = (array, size) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

export function useDeliveries(props) {
  const { daysOffset } = props;
  const { user } = useAuth();
  const [isAdmin] = useState(() => user.user_type.includes("admin"));
  const [isAdminStaff] = useState(() => user.user_type.includes("admin") && user?.is_staff);
  const [plan] = useState(() => user.admin_plan);
  const [isSeller] = useState(() => user.user_type.includes("seller"));
  const [isOperator] = useState(() => user.user_type.includes("operator"));

  // CENTRALIZED STATE
  const [initialState] = useState(() =>
    getInitialState(
      daysOffset,
      isAdmin,
      isAdmin && !user.is_staff ? user?.admin?.id : null,
    ),
  );
  const [filters, setFilters] = useState(initialState.filters);
  const [pagination, setPagination] = useState(initialState.pagination);
  const [tableSort, setTableSort] = useState({
    orderFilter: "",
    orderByFilter: "",
  });
  const [auxSearcher, setAuxSearcher] = useState(filters.search);

  // Data status and IU
  const [pageData, setPageData] = useState([]);
  const [loadingPageData, setLoadingPageData] = useState(true);
  const [loading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [generalParamsFetched, setGeneralParamsFetched] = useState(false);

  // Status for counters and selects options
  const [deliveriesCount, setDeliveriesCount] = useState(0);
  const [counters, setCounters] = useState([]);
  const [counterLabels, setCounterLabels] = useState([]);
  const [deliveryStatusOptions, setDeliveryStatusOptions] = useState([]);
  const [companies, setCompanies] = useState([]);

  // Status for 'Retry Tracking' functionality
  const [loadingObtainTracking, setLoadingObtainTracking] = useState(false);
  const [progress, setProgress] = useState({
    total: 0,
    processed: 0,
    success: [],
    failed: [],
    currentChunk: 0,
    totalChunks: 0,
    running: false,
  });

  // STATE MANNERS (HANDLERS)
  const handleFilterChange = useCallback((filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleDateChange = useCallback((newStartDate, newEndDate) => {
    setFilters((prev) => ({
      ...prev,
      startDate: newStartDate,
      endDate: newEndDate,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleChangePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleChangeRowsPerPage = useCallback((newRowsPerPage) => {
    setPagination({ page: 1, rowsPerPage: newRowsPerPage });
  }, []);

  const handleOrderFilterChange = useCallback((newOrderFilter) => {
    setTableSort((prev) => ({
      ...prev,
      orderFilter: newOrderFilter(prev.orderFilter),
    }));
  }, []);

  const handleOrderByFilterChange = useCallback((newOrderByFilter) => {
    setTableSort((prev) => ({
      ...prev,
      orderByFilter: newOrderByFilter(prev.orderByFilter),
    }));
  }, []);

  const getTrackingNumber = useCallback(async () => {
    if (!pageData || pageData.length === 0) return;

    setLoadingObtainTracking(true);
    const deliveryIds = pageData
      .map((item) => item.delivery_id)
      .filter((id) => id);
    const chunks = chunkArray(deliveryIds, 20);

    setProgress({
      total: deliveryIds.length,
      processed: 0,
      success: [],
      failed: [],
      currentChunk: 0,
      totalChunks: chunks.length,
      running: true,
    });

    try {
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        try {
          const response = await axios.post("api/set-tracking-number/", {
            data: chunk,
          });
          const { success = [], failed = [] } = response.data;
          setProgress((prev) => ({
            ...prev,
            success: [...prev.success, ...success],
            failed: [...prev.failed, ...failed],
            processed: prev.processed + chunk.length,
            currentChunk: i + 1,
          }));
        } catch (error) {
          console.error("Error en el lote:", error);
          setProgress((prev) => ({
            ...prev,
            failed: [...prev.failed, ...chunk],
            processed: prev.processed + chunk.length,
            currentChunk: i + 1,
          }));
        }
      }
    } finally {
      setLoadingObtainTracking(false);
      setProgress((prev) => ({ ...prev, running: false }));
    }
  }, [pageData]);

  const closeGetTrackingResult = useCallback(() => {
    setProgress({
      total: 0,
      processed: 0,
      success: [],
      failed: [],
      currentChunk: 0,
      totalChunks: 0,
      running: false,
    });
  }, []);

  const changeDriver = useCallback(
    async ({ ids, driverId }) => {
      setIsLoading(true);
      setError(null);
      try {
        const {
          data: { message },
        } = await axios.post("deliveries/change_driver/", {
          ids,
          driver_id: driverId,
        });
        setSuccess(message);
      } catch (error) {
        console.error(`Error al intentar reasignar envios: ${error}`);
        setError(`Error al intentar reasignar envios`);
        setSuccess(null);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, setError, setSuccess],
  );

  const massiveChangeStatus = useCallback(
    async ({ ids, status, subStatus }) => {
      setIsLoading(true);
      setError(null);
      try {
        const {
          data: { message },
        } = await axios.post("deliveries/change_status/", {
          ids,
          status,
          substatus: subStatus,
        });
        setSuccess(message);
        return message;
      } catch (error) {
        console.error(`Error al intentar cambiar estados de envios: ${error}`);
        setError(`Error al intentar cambiar estados de envios`);
        setSuccess(null);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, setError, setSuccess],
  );

  const fetchDeliveries = useCallback(async () => {
    setLoadingPageData(true);
    setError(null);
    console.log(filters);

    const params = {
      admin_id: filters.adminFilter,
      start_date: dateToDatabase(filters.startDate),
      end_date: dateToDatabase(filters.endDate),
      company: filters.companyFilter,
      search: filters.search,
      delivery_status: filters.statusFilter,
      assignedDriverFilter: filters.assignedDriverFilter,
      page: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
      orderFilter: tableSort.orderFilter,
      orderByFilter: tableSort.orderByFilter,
    };

    try {
      const {
        data: { deliveries, total_deliveries, status_count },
      } = await axios.get("api/delivery-resume/", { params });
      setPageData(deliveries);
      setDeliveriesCount(total_deliveries);
      setCounters(status_count);
    } catch (error) {
      console.error(`Error al obtener deliveries: ${error}`);
      setError("Ocurrió un error al obtener los deliveries");
      setPageData([]);
    } finally {
      setLoadingPageData(false);
    }
  }, [
    filters,
    pagination,
    tableSort,
    setLoadingPageData,
    setError,
    setPageData,
    setDeliveriesCount,
    setCounters,
  ]);

  // SECONDARY EFFECTS (FETCH, DEBUNCE, LOCALSTORAGE)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (auxSearcher !== filters.search) {
        handleFilterChange("search", auxSearcher);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [auxSearcher, filters.search, handleFilterChange]);

  useEffect(() => {
    localStorage.setItem(
      "deliveriesFilter",
      JSON.stringify({
        ...filters,
        ...pagination,
        orderByFilter: tableSort.orderByFilter,
        orderFilter: tableSort.orderFilter,
      }),
    );
  }, [filters, pagination, tableSort]);

  useEffect(() => {
    if (generalParamsFetched) {
      fetchDeliveries();
    }
  }, [generalParamsFetched, fetchDeliveries]);

  useEffect(() => {
    const fetchDeliveryFilters = async () => {
      try {
        const {
          data: { delivery_statuses, companies },
        } = await axios.get("delivery_params/api/delivery-resume");
        setDeliveryStatusOptions(delivery_statuses);
        setCounterLabels(delivery_statuses);
        if (user?.user_type.includes("admin")) {
          setCompanies(companies);
        }
        setGeneralParamsFetched(true);
      } catch (err) {
        console.error("Error al obtener opciones de filtros:", err);
        setError("Ocurrió un error al obtener las opciones de filtro");
      }
    };

    fetchDeliveryFilters();
  }, [user?.user_type]);

  // PUBLIC API OF THE HOOK
  return {
    // State and Data
    filters,
    pagination,
    tableSort,
    pageData,
    loadingPageData,
    error,
    deliveriesCount,
    counters,
    counterLabels,
    deliveryStatusOptions,
    companies,
    generalParamsFetched,
    user,
    auxSearcher,
    progress,
    loadingObtainTracking,
    isAdmin,
    isAdminStaff,
    isSeller,
    isOperator,
    plan,
    loading,
    success,

    // MANNERS (Setters y Actions)
    setSuccess,
    setError,
    handleFilterChange,
    handleDateChange,
    handleChangePage,
    handleChangeRowsPerPage,
    handleOrderFilterChange,
    handleOrderByFilterChange,
    setAuxSearcher,
    getTrackingNumber,
    closeGetTrackingResult,
    changeDriver,
    setIsLoading,
    refetchDeliveries: fetchDeliveries,
    massiveChangeStatus,
  };
}
